import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { DOOR_ENTERED_MS, DOOR_OPEN_MS, doorState } from './door-motion.js';
import { buildBricks } from './door-bricks.js';
import { STONE_TILE, glowTexture, hammeredNormal, stoneTextures } from './door-textures.js';

/**
 * The sealed door as a lit 3D scene. Two thick walls of stone brick meet at a seam in front of a recessed back plate, run on iron
 * bands and are locked by three bolts. The gem is the handle of the middle bolt and is fixed to the right slab, so it is dragged
 * away with that slab; it is also the room's light, an amber point light that moves with it and throws the relief and shadows
 * of the brickwork, bands and bolts across the stone. The view is a perspective camera that sways slightly and follows the pointer.
 */

const SLAB_W = 14, SLAB_H = 24, FRONT = .37, MORTAR = FRONT - .15, RELIEF = .075, BAND_Y = [-2.15, 0, 2.15], BOLT_L = 1.55, SOCKET_R = 1, SLOT_L = 1.7, GEM_R = .92;
const FOV = 32, MIN_VISIBLE_W = 9, MIN_VISIBLE_H = 7;
/** Only the bricks that can ever be in view are built: the visible height at the current shape, with room for the lens widening and the sway. */
const masonryHeight = (aspect: number) => Math.min(SLAB_H, Math.max(MIN_VISIBLE_H, MIN_VISIBLE_W / aspect) * 1.3 + 1.5);
/** The render scale is capped, and drops toward 1 if the frame rate sags. */
const MAX_PIXEL_RATIO = 1.5, SLOW_FRAME_MS = 26;
/** How far the camera is drawn forward, in world units, and how much the lens widens, as it comes through the doorway. */
const RUSH_DISTANCE = 9, RUSH_FOV = 9;
/** Measured in the same units as the world: the radius and slot width of each bolt. */
const BOLT = { side: { radius: .13, slot: .18, z: .17 }, main: { radius: .17, slot: .21, z: .19 } };

const bolts = BAND_Y.map(y => ({ y, ...(y === 0 ? BOLT.main : BOLT.side) }));

/** Gives a box UVs in world units on each face, so a texture keeps its true scale however long the box is. */
function worldUV(geometry: THREE.BufferGeometry): THREE.BufferGeometry {
  const position = geometry.getAttribute('position'), normal = geometry.getAttribute('normal'), uv = geometry.getAttribute('uv');
  for (let i = 0; i < position.count; i++) {
    const ax = Math.abs(normal.getX(i)), ay = Math.abs(normal.getY(i)), az = Math.abs(normal.getZ(i));
    if (az >= ax && az >= ay) uv.setXY(i, position.getX(i), position.getY(i)); else if (ax >= ay) uv.setXY(i, position.getZ(i), position.getY(i)); else uv.setXY(i, position.getX(i), position.getZ(i));
  }
  return geometry;
}

export class DoorScene {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(FOV, 16 / 9, .1, 80);
  private readonly left = new THREE.Group();
  private readonly right = new THREE.Group();
  private readonly rods: THREE.Object3D[] = [];
  private readonly collar = new THREE.Group();
  private readonly gemGroup = new THREE.Group();
  private gem!: THREE.Mesh<THREE.BufferGeometry, THREE.MeshPhysicalMaterial>;
  private readonly gemLight = new THREE.PointLight(0xffa954, 1, 18, 2);
  private readonly glow: THREE.Sprite;
  private readonly key = new THREE.DirectionalLight(0xcfd9f2, .85);
  private readonly lintel: THREE.Mesh; private readonly sill: THREE.Mesh;
  private readonly disposables: { dispose(): void }[] = [];
  private readonly pointer = new THREE.Vector2(); private readonly eased = new THREE.Vector2();
  private readonly reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  private readonly project = new THREE.Vector3(); private readonly scratch = new THREE.Vector3();
  private readonly observer: ResizeObserver;
  private openedAt: number | null = null; private openFinished = false; private entered = false;
  private resolveGone: (() => void) | null = null; private resolveEntered: (() => void) | null = null;
  private restZ = 12; private rush = 0;
  private hover = 0; private hovering = false;
  /** Dimmed while the world prepares; `invitedAt` starts the pulse that says the gem can be touched. */
  private waiting = false; private dim = 0; private invitedAt = -1;
  private raf = 0; private disposed = false;
  private stone!: ReturnType<typeof stoneTextures>; private fouls!: (x0: number, y0: number, x1: number, y1: number) => boolean;
  private masonry: { group: THREE.Group; release: { dispose(): void }[] }[] = []; private brickHeight = 0;
  /** Shadow maps are only redrawn while something that casts them is moving, plus a couple of frames after anything changes. */
  private shadowFrames = 3;
  private pixelRatio = 1; private lastTime = 0; private averageFrame = 16; private framesSeen = 0;
  private width = 1; private height = 1;

  /** Builds the door, or returns null where WebGL is unavailable (the page then falls back to a flat door). */
  static create(host: HTMLElement, canvas: HTMLCanvasElement): DoorScene | null {
    try { return new DoorScene(host, canvas); } catch { return null; }
  }

  private constructor(private host: HTMLElement, private canvas: HTMLCanvasElement) {
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, powerPreference: 'high-performance' });
    const renderer = this.renderer;
    this.pixelRatio = Math.min(devicePixelRatio, MAX_PIXEL_RATIO); renderer.setPixelRatio(this.pixelRatio); renderer.setClearColor(0x000000, 0);
    renderer.toneMapping = THREE.ACESFilmicToneMapping; renderer.toneMappingExposure = .9;
    renderer.shadowMap.enabled = true; renderer.shadowMap.autoUpdate = false; renderer.shadowMap.type = THREE.PCFShadowMap;
    const anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());

    // Image-based light is only a faint fill so the metals have something to reflect; the gem does the real lighting.
    const environment = new THREE.PMREMGenerator(renderer), room = new RoomEnvironment();
    this.scene.environment = environment.fromScene(room, .04).texture; this.scene.environmentIntensity = .22;
    room.dispose(); environment.dispose();

    // One stone texture serves every brick (each looks into its own window of it), the mortar and the beams.
    const stoneLeft = stoneTextures(7, anisotropy), hammered = hammeredNormal(5, anisotropy), glow = glowTexture();
    this.disposables.push(stoneLeft.map, stoneLeft.normalMap, hammered, glow);
    // The frame's beams are big hewn blocks: the stone texture at its true scale, tinted.
    const beamMaterial = (stone: typeof stoneLeft) => {
      const map = stone.map.clone(), normalMap = stone.normalMap.clone();
      for (const t of [map, normalMap]) { t.repeat.set(1 / STONE_TILE, 1 / STONE_TILE); t.needsUpdate = true; this.disposables.push(t); }
      return this.own(new THREE.MeshStandardMaterial({ map, normalMap, normalScale: new THREE.Vector2(1.4, 1.4), roughness: .9, color: 0x9a8f80 }));
    };
    const metal = (color: number, roughness: number, metalness: number, scale: [number, number]) => {
      const bump = hammered.clone(); bump.repeat.set(...scale); bump.needsUpdate = true; this.disposables.push(bump);
      return this.own(new THREE.MeshStandardMaterial({ color, roughness, metalness, normalMap: bump, normalScale: new THREE.Vector2(.5, .5) }));
    };
    const iron = metal(0x45423d, .52, .85, [6, 1]), bronze = metal(0xb98a43, .36, .9, [2, 2]), darkBronze = metal(0x6a4c22, .5, .85, [1, 1]);
    const floor = this.own(new THREE.MeshStandardMaterial({ color: 0x24211d, roughness: 1 }));
    // The mortar bed the bricks stand out from: dark, rough and recessed.
    const mortarMap = stoneLeft.map.clone(), mortarNormal = stoneLeft.normalMap.clone();
    for (const t of [mortarMap, mortarNormal]) { t.repeat.set(1 / 2.2, 1 / 2.2); t.needsUpdate = true; this.disposables.push(t); }
    const mortar = this.own(new THREE.MeshStandardMaterial({ color: 0x55493c, roughness: 1, map: mortarMap, normalMap: mortarNormal, normalScale: new THREE.Vector2(2, 2) }));

    this.stone = stoneLeft; this.buildSlabs(mortar, floor, iron, bronze, darkBronze);
    this.buildGem(bronze, darkBronze, floor);
    this.glow = new THREE.Sprite(this.own(new THREE.SpriteMaterial({ map: glow, color: 0xffb35e, blending: THREE.AdditiveBlending, depthWrite: false, transparent: true, opacity: .5 })));
    this.glow.scale.setScalar(4.4); this.glow.position.set(0, 0, .62); this.gemGroup.add(this.glow);

    // The frame the slabs run in: a lintel and a sill that stand clear of the door and shadow it.
    const beam = (stone: typeof stoneLeft) => new THREE.Mesh(this.own(worldUV(new RoundedBoxGeometry(48, 1.5, 1.7, 3, .05))), beamMaterial(stone));
    this.lintel = beam(stoneLeft); this.sill = beam(stoneLeft);
    this.scene.add(this.left, this.right, this.lintel, this.sill);

    const hemisphere = new THREE.HemisphereLight(0x7f8cab, 0x2b2118, .32);
    this.key.position.set(-6, 7, 10); this.key.target.position.set(0, 0, 0);
    this.key.castShadow = true; this.key.shadow.mapSize.set(1024, 1024); this.key.shadow.bias = -.0004; this.key.shadow.normalBias = .03; this.key.shadow.radius = 3;
    this.gemLight.position.set(0, 0, 2); this.gemLight.castShadow = true;
    this.gemLight.shadow.mapSize.set(1024, 1024); this.gemLight.shadow.camera.near = .35; this.gemLight.shadow.camera.far = 22; this.gemLight.shadow.bias = -.002; this.gemLight.shadow.normalBias = .03;
    this.gemGroup.add(this.gemLight);
    this.scene.add(hemisphere, this.key, this.key.target);
    this.scene.traverse(object => { if (object instanceof THREE.Mesh) { object.castShadow = true; object.receiveShadow = true; } });

    this.observer = new ResizeObserver(() => this.layout()); this.observer.observe(host);
    window.addEventListener('pointermove', this.onPointer);
    this.layout();
    this.raf = requestAnimationFrame(this.frame);
  }

  private own<T extends { dispose(): void }>(resource: T): T { this.disposables.push(resource); return resource; }

  /** Both slabs: a recessed back plate with a stone shell over it. The left shell is cut away where the right slab's bolts and gem sit. */
  private buildSlabs(mortar: THREE.Material, floor: THREE.Material, iron: THREE.Material, bronze: THREE.Material, darkBronze: THREE.Material) {
    const shell = (shape: THREE.Shape, material: THREE.Material) => new THREE.Mesh(this.own(new THREE.ExtrudeGeometry(shape, { depth: MORTAR - .06, bevelEnabled: true, bevelThickness: .03, bevelSize: .03, bevelSegments: 2, curveSegments: 40 })), material);
    const half = SLAB_H / 2;

    // The left slab's outline runs up the seam edge, cutting a slot for each bolt and a round socket (with its slot) for the gem.
    const outline = new THREE.Shape();
    outline.moveTo(-SLAB_W, -half); outline.lineTo(0, -half);
    for (const bolt of bolts) {
      if (bolt.y !== 0) { outline.lineTo(0, bolt.y - bolt.slot); outline.lineTo(-SLOT_L, bolt.y - bolt.slot); outline.lineTo(-SLOT_L, bolt.y + bolt.slot); outline.lineTo(0, bolt.y + bolt.slot); continue; }
      const a = Math.asin(bolt.slot / SOCKET_R), reach = Math.sqrt(SOCKET_R ** 2 - bolt.slot ** 2);
      outline.lineTo(0, -SOCKET_R); outline.absarc(0, 0, SOCKET_R, -Math.PI / 2, -Math.PI + a, true);
      outline.lineTo(-SLOT_L, -bolt.slot); outline.lineTo(-SLOT_L, bolt.slot); outline.lineTo(-reach, bolt.slot);
      outline.absarc(0, 0, SOCKET_R, Math.PI - a, Math.PI / 2, true);
    }
    outline.lineTo(0, half); outline.lineTo(-SLAB_W, half); outline.closePath();
    const leftShell = shell(outline, mortar); leftShell.position.z = .03; this.left.add(leftShell);
    const rightShell = shell(new THREE.Shape().moveTo(0, -half).lineTo(SLAB_W, -half).lineTo(SLAB_W, half).lineTo(0, half).closePath(), mortar); rightShell.position.z = .03; this.right.add(rightShell);

    // The masonry itself. Bricks stop short of the left slab's slots and socket, leaving a channel of bare mortar around each.
    const margin = .09; this.fouls = (x0: number, y0: number, x1: number, y1: number) => bolts.some(bolt => {
      if (x1 > -SLOT_L - margin && x0 < margin && y1 > bolt.y - bolt.slot - margin && y0 < bolt.y + bolt.slot + margin) return true;
      if (bolt.y !== 0) return false;
      const nearX = Math.min(Math.max(0, x0), x1), nearY = Math.min(Math.max(0, y0), y1);
      return Math.hypot(nearX, nearY) < SOCKET_R + margin;
    });
    this.buildMasonry(masonryHeight(Math.max(1, this.host.clientWidth) / Math.max(1, this.host.clientHeight)));
    for (const [group, sign] of [[this.left, -1], [this.right, 1]] as const) {
      const back = new THREE.Mesh(this.own(new THREE.BoxGeometry(SLAB_W, SLAB_H, .6)), floor); back.position.set(sign * SLAB_W / 2, 0, -.3); group.add(back);
      // The jamb: a bronze strip down the inner edge, studded with rivets.
      const jamb = new THREE.Mesh(this.own(new RoundedBoxGeometry(.32, SLAB_H, .16, 2, .04)), darkBronze); jamb.position.set(sign * .16, 0, FRONT + .08); group.add(jamb);
      group.add(this.rivets(bronze, Array.from({ length: Math.ceil(SLAB_H / .9) }, (_, i) => [sign * .16, -half + .45 + i * .9, FRONT + .15] as const)));
      // Iron bands across the face. The left bands stop short of the slots so the sockets stay open.
      for (const y of BAND_Y) {
        const start = sign < 0 ? SLOT_L + .22 : .36, length = SLAB_W - start;
        const band = new THREE.Mesh(this.own(new RoundedBoxGeometry(length, .56, .18, 2, .04)), iron); band.position.set(sign * (start + length / 2), y, FRONT + .07); group.add(band);
        group.add(this.rivets(bronze, Array.from({ length: Math.floor(length) }, (_, i) => [sign * (start + .5 + i), y, FRONT + .16] as const)));
      }
    }
    // A ring inlaid in the floor of the socket, set with tick marks, that shows once the gem has gone.
    const inlay = new THREE.Mesh(this.own(new THREE.TorusGeometry(.78, .03, 8, 64, Math.PI)), darkBronze); inlay.rotation.z = Math.PI / 2; inlay.position.z = .03; this.left.add(inlay);
    const ticks = new THREE.InstancedMesh(this.own(new THREE.BoxGeometry(.04, .1, .03)), darkBronze, 24);
    const tick = new THREE.Object3D();
    for (let i = 0; i < 24; i++) { const angle = Math.PI / 2 + (i + .5) / 24 * Math.PI; tick.position.set(Math.cos(angle) * .62, Math.sin(angle) * .62, .03); tick.rotation.z = angle - Math.PI / 2; tick.updateMatrix(); ticks.setMatrixAt(i, tick.matrix); }
    this.left.add(ticks);

    // The bolts: ribbed bars that slide out of sleeves in the right slab and into the slots cut in the left one. A lathe runs along +y;
    // turned a quarter, its base is on the seam and its tip leads left. Drawing the bolt back moves the holder toward +x, into the slab.
    for (const bolt of bolts) {
      const profile: THREE.Vector2[] = [new THREE.Vector2(0, 0)];
      for (let at = 0; at <= BOLT_L; at += .03) profile.push(new THREE.Vector2(bolt.radius * (at > .15 && at < BOLT_L - .15 && at % .3 < .09 ? 1.12 : 1) * (at > BOLT_L - .08 ? .88 : 1), at));
      profile.push(new THREE.Vector2(0, BOLT_L));
      const rod = new THREE.Mesh(this.own(new THREE.LatheGeometry(profile, 24)), bronze); rod.rotation.z = Math.PI / 2;
      const holder = new THREE.Group(); holder.position.set(0, bolt.y, bolt.z); holder.add(rod);
      this.right.add(holder); this.rods.push(holder);
      const sleeve = new THREE.Mesh(this.own(new THREE.CylinderGeometry(bolt.radius * 1.9, bolt.radius * 1.9, .3, 24)), darkBronze);
      sleeve.rotation.z = Math.PI / 2; sleeve.position.set(-.1, bolt.y, bolt.z); this.right.add(sleeve);
    }
  }

  /** (Re)lays the bricks to cover `height`. They are rebuilt only if the window becomes tall enough to show more than was laid. */
  private buildMasonry(height: number) {
    for (const old of this.masonry) { old.group.removeFromParent(); old.release.forEach(resource => resource.dispose()); }
    this.masonry = []; this.brickHeight = height; this.shadowFrames = 3;
    for (const [group, side, seed] of [[this.left, -1, 11], [this.right, 1, 29]] as const) {
      const release: { dispose(): void }[] = [];
      const bricks = buildBricks({ side, width: SLAB_W, height, front: FRONT, relief: RELIEF, seed, stone: this.stone, keepOut: side < 0 ? this.fouls : () => false, own: r => { release.push(r); return r; } });
      bricks.traverse(object => { if (object instanceof THREE.Mesh) { object.castShadow = true; object.receiveShadow = true; } });
      group.add(bricks); this.masonry.push({ group: bricks, release });
    }
  }

  /** Domed rivets at the given positions, drawn as one instanced mesh. */
  private rivets(material: THREE.Material, points: readonly (readonly [number, number, number])[]): THREE.InstancedMesh {
    const mesh = new THREE.InstancedMesh(this.own(new THREE.SphereGeometry(.075, 12, 8)), material, points.length), matrix = new THREE.Matrix4();
    points.forEach(([x, y, z], i) => mesh.setMatrixAt(i, matrix.makeTranslation(x, y, z)));
    return mesh;
  }

  /** The gem and its housing: a bronze seat in a ring of teeth that turns to draw the bolts, and a stone cut in eight facets. */
  private buildGem(bronze: THREE.Material, darkBronze: THREE.Material, floor: THREE.Material) {
    const seat = new THREE.Mesh(this.own(new THREE.CylinderGeometry(GEM_R, GEM_R + .03, .32, 56)), bronze);
    seat.rotation.x = Math.PI / 2; seat.position.z = .27; this.gemGroup.add(seat);
    const gear = new THREE.Shape(), teeth = 20, steps = teeth * 4;
    for (let i = 0; i <= steps; i++) {
      const angle = i / steps * Math.PI * 2, radius = i % 4 === 1 || i % 4 === 2 ? .76 : .67;
      if (i === 0) gear.moveTo(Math.cos(angle) * radius, Math.sin(angle) * radius); else gear.lineTo(Math.cos(angle) * radius, Math.sin(angle) * radius);
    }
    const teethMesh = new THREE.Mesh(this.own(new THREE.ExtrudeGeometry(gear, { depth: .1, bevelEnabled: true, bevelThickness: .015, bevelSize: .015, bevelSegments: 1 })), darkBronze);
    teethMesh.position.z = .43; this.collar.add(teethMesh);
    const ring = new THREE.Mesh(this.own(new THREE.TorusGeometry(.6, .035, 10, 56)), bronze); ring.position.z = .56; this.collar.add(ring);
    const pits = this.own(new THREE.CylinderGeometry(.045, .045, .06, 10));
    for (let i = 0; i < 8; i++) { const pit = new THREE.Mesh(pits, floor); const angle = i / 8 * Math.PI * 2; pit.rotation.x = Math.PI / 2; pit.position.set(Math.cos(angle) * .71, Math.sin(angle) * .71, .535); this.collar.add(pit); }
    this.gemGroup.add(this.collar);
    const bed = new THREE.Mesh(this.own(new THREE.CylinderGeometry(.56, .56, .06, 40)), floor); bed.rotation.x = Math.PI / 2; bed.position.z = .5; this.gemGroup.add(bed);
    // Eight facets: a lathe of a cut stone's profile, flat shaded so each face catches the light on its own.
    const cut = this.own(new THREE.LatheGeometry([[0, -.34], [.5, -.02], [.5, .06], [.32, .26], [0, .26]].map(([x, y]) => new THREE.Vector2(x, y)), 8));
    const material = this.own(new THREE.MeshPhysicalMaterial({ color: 0xb86a14, emissive: 0xc9660f, emissiveIntensity: .6, roughness: .06, clearcoat: 1, clearcoatRoughness: .05, ior: 1.76, flatShading: true, envMapIntensity: 1.6 }));
    this.gem = new THREE.Mesh(cut, material);
    this.gem.rotation.set(Math.PI / 2, Math.PI / 8, 0); this.gem.position.z = .76; this.gem.scale.setScalar(1.15);
    this.gemGroup.add(this.gem);
    this.right.add(this.gemGroup);
  }

  private onPointer = (event: PointerEvent) => {
    this.pointer.set(event.clientX / innerWidth * 2 - 1, -(event.clientY / innerHeight * 2 - 1));
    this.hovering = this.openedAt === null && this.hits(event.clientX, event.clientY);
    this.canvas.style.cursor = this.hovering && !this.waiting ? 'var(--cursor-press, pointer)' : '';
  };

  /** Whether a point on the page is on the gem, with a generous margin for fingers. */
  hits(clientX: number, clientY: number): boolean {
    const box = this.canvas.getBoundingClientRect();
    this.project.setFromMatrixPosition(this.gemGroup.matrixWorld); this.project.z += .76; this.scratch.copy(this.project); this.scratch.x += GEM_R;
    this.project.project(this.camera); this.scratch.project(this.camera);
    const x = box.left + (this.project.x * .5 + .5) * this.width, y = box.top + (-this.project.y * .5 + .5) * this.height;
    const radius = Math.max(24, Math.abs(this.scratch.x - this.project.x) * this.width);
    return Math.hypot(clientX - x, clientY - y) <= radius;
  }

  /** Dims the gem while the world prepares; when that ends the gem pulses to invite a touch. */
  setWaiting(waiting: boolean) {
    if (this.waiting && !waiting) this.invitedAt = performance.now();
    this.waiting = waiting;
    if (waiting) this.canvas.style.cursor = '';
  }

  /** Sizes the canvas and camera. The door is framed by width on narrow screens, so the gem never gets smaller than a fraction of it. */
  private layout() {
    if (this.disposed) return;
    this.width = Math.max(1, this.host.clientWidth); this.height = Math.max(1, this.host.clientHeight);
    this.renderer.setSize(this.width, this.height, false);
    const aspect = this.width / this.height, visibleH = Math.max(MIN_VISIBLE_H, MIN_VISIBLE_W / aspect);
    this.restZ = visibleH / 2 / Math.tan(THREE.MathUtils.degToRad(FOV / 2));
    this.camera.aspect = aspect; this.camera.fov = FOV + RUSH_FOV * this.rush; this.camera.position.z = this.restZ - RUSH_DISTANCE * this.rush;
    this.camera.near = .3; this.camera.far = 60; this.camera.updateProjectionMatrix();
    this.lintel.position.set(0, visibleH / 2 + .02, .55); this.sill.position.set(0, -visibleH / 2 - .02, .55);
    if (masonryHeight(aspect) > this.brickHeight + .01) this.buildMasonry(masonryHeight(aspect));
    this.shadowFrames = 3;
    const reach = visibleH * aspect / 2 + 3, shadow = this.key.shadow.camera;
    shadow.left = -reach; shadow.right = reach; shadow.top = visibleH / 2 + 2; shadow.bottom = -visibleH / 2 - 2; shadow.near = 1; shadow.far = 40; shadow.updateProjectionMatrix();
  }

  /** The two inner slab edges in CSS pixels across the canvas, for the sparks and dust the door throws. */
  edges(): [number, number] {
    const toPixels = (group: THREE.Group) => { this.project.set(group.position.x, 0, FRONT).project(this.camera); return (this.project.x * .5 + .5) * this.width; };
    return [toPixels(this.left), toPixels(this.right)];
  }

  /**
   * Starts the opening. `gone` resolves once both slabs have left the screen; `entered` once the camera has then been drawn through
   * the doorway, past the lintel and sill, and nothing of the door is left in view.
   */
  open(): { gone: Promise<void>; entered: Promise<void> } {
    const gone = new Promise<void>(resolve => { this.resolveGone = resolve; }), entered = new Promise<void>(resolve => { this.resolveEntered = resolve; });
    this.openedAt = performance.now();
    return { gone, entered };
  }

  private frame = (time: number) => {
    if (this.disposed) return;
    this.raf = requestAnimationFrame(this.frame);
    const elapsed = this.openedAt === null ? -1 : time - this.openedAt;
    const state = doorState(this.reducedMotion && elapsed > 0 ? 1e9 : elapsed);
    // As the locks release both slabs tremble: they rattle up and down in opposition and only ever back off the seam, never into each other.
    const shudder = state.tremble, away = Math.abs(shudder) * .04;
    this.left.position.set(state.slab / 100 * SLAB_W - away, shudder * .03, 0); this.right.position.set(-state.slab / 100 * SLAB_W + away, -shudder * .03, 0);
    for (const rod of this.rods) rod.position.x = state.bolt * BOLT_L;
    this.collar.rotation.z = -state.collar * Math.PI * .75; this.gemGroup.position.z = -.07 * state.press;
    if (elapsed >= (this.reducedMotion ? 10 : DOOR_OPEN_MS) && !this.openFinished) { this.openFinished = true; this.resolveGone?.(); }
    if (elapsed >= (this.reducedMotion ? 10 : DOOR_ENTERED_MS) && !this.entered) { this.entered = true; this.resolveEntered?.(); }
    // Drawn forward through the doorway, the lens widening a little: the lintel and sill rush past overhead and underfoot and out of frame.
    const rush = this.reducedMotion ? 0 : state.rush;
    if (rush !== this.rush) {
      this.rush = rush; this.camera.position.z = this.restZ - RUSH_DISTANCE * rush; this.camera.fov = FOV + RUSH_FOV * rush; this.camera.updateProjectionMatrix();
    }

    // The gem breathes and its light flickers a little, like a flame. A touch or the pointer over it brightens it; the touch flares it.
    const t = time / 1000;
    this.hover += ((this.hovering && !this.waiting && this.openedAt === null ? 1 : 0) - this.hover) * .15;
    // While the world prepares the gem burns low; once it is ready it swells twice, then settles to its usual breath.
    this.dim += ((this.waiting ? 1 : 0) - this.dim) * .08;
    const since = this.invitedAt < 0 || this.reducedMotion ? Infinity : (time - this.invitedAt) / 1100, invite = since < 2 ? Math.sin(Math.PI * (since % 1)) : 0;
    const flicker = 1 + .05 * Math.sin(t * 7.1) + .035 * Math.sin(t * 13.7) + .02 * Math.sin(t * 29.3), breath = .5 + .5 * Math.sin(t * 2.4), low = 1 - .6 * this.dim;
    this.gemLight.intensity = 34 * flicker * low * (1 + .12 * breath + .35 * this.hover + .6 * invite + 2.4 * state.flare);
    this.gem.material.emissiveIntensity = (.5 + .15 * breath + .3 * this.hover + .4 * invite) * low + 1.1 * state.flare;
    (this.glow.material as THREE.SpriteMaterial).opacity = (.32 + .1 * breath + .12 * this.hover + .25 * invite) * low + .45 * state.flare;
    this.glow.scale.setScalar(4.2 + .3 * breath + 1.6 * invite + 1.8 * state.flare);

    // A slow sway and a lean toward the pointer give the door parallax.
    const lean = this.reducedMotion ? 0 : 1 - this.rush;
    this.eased.lerp(this.pointer, .04);
    this.camera.position.x = (this.eased.x * .45 + Math.sin(t * .3) * .08) * lean; this.camera.position.y = (this.eased.y * .28 + Math.sin(t * .23) * .05) * lean;
    this.camera.lookAt(0, 0, 0);
    // The shadow maps only need redrawing while the slabs are moving (the sealed door's shadows are fixed), and for a few frames after a change.
    this.renderer.shadowMap.needsUpdate = (this.openedAt !== null && !this.entered) || this.shadowFrames-- > 0;
    this.renderer.render(this.scene, this.camera);
    this.adapt(time);
  };

  /** Steps the render scale down, never up, if frames keep arriving slowly once the first few have warmed the shaders. */
  private adapt(time: number) {
    const dt = time - this.lastTime; this.lastTime = time;
    if (++this.framesSeen < 30 || dt <= 0 || dt > 250) return;
    this.averageFrame += (dt - this.averageFrame) * .1;
    if (this.averageFrame > SLOW_FRAME_MS && this.pixelRatio > 1) {
      this.pixelRatio = Math.max(1, this.pixelRatio - .25); this.averageFrame = 16; this.framesSeen = 0;
      this.renderer.setPixelRatio(this.pixelRatio); this.renderer.setSize(this.width, this.height, false);
    }
  }

  dispose() {
    if (this.disposed) return;
    this.disposed = true; cancelAnimationFrame(this.raf); this.observer.disconnect();
    window.removeEventListener('pointermove', this.onPointer);
    for (const resource of this.disposables) resource.dispose();
    this.scene.environment?.dispose(); this.renderer.dispose(); this.renderer.forceContextLoss();
  }
}
